import { IFieldEditor } from '@/shared/types/fieldEditor';
import * as docx from 'docx';
import { DefaultTheme } from 'styled-components/dist/types';
import { formatAiFieldValueForTemplate, formatFieldValueForTemplate, truncateString } from '../../../utils';
import { getAiSuggestedSectionDOCX, transparentDocxTableBorders } from '../../utils';

export const getThreeColumnSection = async (fields: IFieldEditor[], includeAiSuggested: boolean, theme: DefaultTheme) => {
  const createCell = async (field: IFieldEditor) => {
    const aiIncluded = field.value === null && field.commentary === null && field.AI?.answers?.length && includeAiSuggested;
    const text = aiIncluded
      ? truncateString(formatAiFieldValueForTemplate(field.documentFieldType, field.AI.answers[0]), 250)
      : truncateString(formatFieldValueForTemplate(field.documentFieldType, field.value, field.commentary), 250) || 'Not specified';
    const isNotSpecified = text === 'Not specified';

    const formattedText = text.split('\n').flatMap((line, index, array) => {
      return index < array.length - 1
        ? [
            new docx.TextRun({
              text: line,
              color: theme.font.base,
              italics: isNotSpecified
            }),
            new docx.TextRun({ break: 1 })
          ]
        : new docx.TextRun({
            text: line,
            color: theme.font.base,
            italics: isNotSpecified
          });
    });

    return new docx.TableCell({
      children: [
        new docx.Paragraph({
          children: [
            new docx.TextRun({
              text: aiIncluded ? `* ${field.name}: ` : `${field.name}: `,
              bold: true,
              color: theme.font.base
            }),
            ...formattedText
          ],
          indent: {
            left: 100,
            right: 100
          },
          spacing: {
            before: 100,
            after: 100
          }
        })
      ],
      borders: transparentDocxTableBorders,
      width: {
        size: 6000,
        type: docx.WidthType.DXA
      }
    });
  };

  const cells = [];
  for await (const field of fields) {
    const cell = await createCell(field);
    cells.push(cell);
  }

  const rows = [];
  for (let i = 0; i < cells.length; i += 3) {
    const row = new docx.TableRow({
      children: [
        cells[i],
        cells[i + 1] ? cells[i + 1] : new docx.TableCell({ children: [], borders: transparentDocxTableBorders }),
        cells[i + 2] ? cells[i + 2] : new docx.TableCell({ children: [], borders: transparentDocxTableBorders })
      ]
    });
    rows.push(row);
  }

  const isAiSuggestedIncluded = (fields: IFieldEditor[], includeAiSuggested: boolean) => {
    return fields.some((field) => (!field.value || field.value === field.AI?.answers[0]) && field.AI?.answers.length) && includeAiSuggested;
  };

  if (isAiSuggestedIncluded(fields, includeAiSuggested)) {
    const aiSuggestedSection = await getAiSuggestedSectionDOCX(fields, theme);
    return [new docx.Table({ rows }), ...aiSuggestedSection];
  }

  return [new docx.Table({ rows })];
};
